/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Progress,
  Select,
  Spin,
  Alert,
  Badge,
  Popconfirm,
  message,
  Modal,
  Input,
} from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import {
  useConfirmEventMutation,
  useGetEventsQueueQuery,
  useRejectEventMutation,
} from '../../api/plantingEventsApi';
import { EventQueueItem, EventQueueStatus, EventQueueType } from '../../types/plantingEvents';
import { useSave } from '../../contexts/WorldContext';
import { CompleteEventModal } from './CompleteEventModal';
import { EventDetailsModal } from './EventDetailsModal';
import { hasAction } from '../../types/permissions';
import { EventKindTag } from '../../utils/eventKindHelpers';

const { Title, Text } = Typography;
const { Option } = Select;

const styles = {
  container: css({ padding: '24px' }),
  header: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }),
  title: css({ margin: 0 }),
  statsRow: css({ display: 'flex', justifyContent: 'space-between' }),
  statsProgress: css({ display: 'flex', justifyContent: 'space-around' }),
  badgePadding: css({ padding: '0 20px' }),
  progressContainer: css({ minWidth: '100px' }),
  spinnerContainer: css({ padding: '40px' }),
};

/**
 * Unified компонент очереди событий (CN-0008 refactoring)
 * Единая страница "Задачи" с быстрым переключателем assignee
 */
export const EventsQueueUnified: React.FC = () => {
  const { saveId } = useParams<{ saveId: string }>();
  const save = useSave();
  const farmId = save.farmId;
  const farmIdValue = farmId || '';

  // State
  const [statusFilter, setStatusFilter] = useState<EventQueueStatus | undefined>('planned');
  // Harvests demoted to a task after a cursor rollback come back as decision_state
  // 'proposed' (status stays 'completed') — surface them via this filter so they can
  // be confirmed (re-apply) or rejected (discard) regardless of status.
  const [decisionFilter, setDecisionFilter] = useState<'proposed' | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<EventQueueType | undefined>(undefined);
  const [assigneeFilter, setAssigneeFilter] = useState<'all' | 'me' | 'unassigned'>('all');
  const [selectedEvent, setSelectedEvent] = useState<EventQueueItem | null>(null);
  const [viewEventId, setViewEventId] = useState<string | null>(null);
  const [rejectModalEventId, setRejectModalEventId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  
  const navigate = useNavigate();

  const user = useSelector((state: any) => state.auth.user);

  // Права
  const canCompleteEvents =
    user?.role === 'superuser' ||
    user?.role === 'agronomist' ||
    hasAction(user, 'events', 'events.complete', farmIdValue) ||
    hasAction(user, 'events', 'events.update', farmIdValue);

  // API
  const {
    data: queueData,
    isLoading,
    error,
    refetch,
  } = useGetEventsQueueQuery(
    {
      saveId: saveId || '',
      // When filtering by "awaiting decision" we drop the status filter so proposed
      // items surface regardless of their status (rolled-back harvests are 'completed').
      status: decisionFilter ? undefined : statusFilter,
      type: typeFilter,
      assignee: assigneeFilter,
      decisionState: decisionFilter,
    },
    { skip: !saveId }
  );
  const [confirmEvent, { isLoading: confirmingEvent }] = useConfirmEventMutation();
  const [rejectEvent, { isLoading: rejectingEvent }] = useRejectEventMutation();

  // Группировка batch событий
  const groupedEvents = React.useMemo(() => {
    if (!queueData?.events) return [];

    const childEventsByParent = new Map<string, EventQueueItem[]>();
    const parentEvents: EventQueueItem[] = [];
    const standaloneEvents: EventQueueItem[] = [];

    queueData.events.forEach((event) => {
      if (event.parentEventId) {
        const siblings = childEventsByParent.get(event.parentEventId) || [];
        siblings.push(event);
        childEventsByParent.set(event.parentEventId, siblings);
      } else if (event.isBatch) {
        parentEvents.push(event);
      } else {
        standaloneEvents.push(event);
      }
    });

    // Создаём синтетические родительские события из дочерних
    childEventsByParent.forEach((children, parentId) => {
      if (children.length > 0 && !parentEvents.find((p) => p.id === parentId)) {
        const firstChild = children[0];
        const completedCount = children.filter((c) => c.status === 'completed').length;

        const syntheticParent: EventQueueItem = {
          ...firstChild,
          id: parentId,
          parentEventId: null,
          isBatch: true,
          batchTotal: children.length,
          batchCompleted: completedCount,
        };

        parentEvents.push(syntheticParent);
      }
    });

    return [...parentEvents, ...standaloneEvents];
  }, [queueData?.events]);

  const getStatusTag = (status: EventQueueStatus) => {
    const statusConfig: Record<
      EventQueueStatus,
      { color: string; icon: React.ReactNode; text: string }
    > = {
      planned: { color: 'default', icon: <ClockCircleOutlined />, text: 'Запланировано' },
      pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Ожидает' },
      in_progress: { color: 'blue', icon: <ClockCircleOutlined />, text: 'Выполняется' },
      completed: { color: 'green', icon: <CheckCircleOutlined />, text: 'Завершено' },
      cancelled: { color: 'red', icon: <CloseCircleOutlined />, text: 'Отменено' },
    };

    const config = statusConfig[status] || statusConfig.planned;
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getDecisionTag = (decisionState?: string) => {
    const state = decisionState || 'confirmed';
    if (state === 'proposed') {
      return <Tag color="gold">Требует проверки</Tag>;
    }
    if (state === 'rejected') {
      return <Tag color="red">Отклонено</Tag>;
    }
    return <Tag color="green">Подтверждено</Tag>;
  };

  const getSourceTag = (sourceType?: string, sourceConfidence?: number | null) => {
    const sourceLabel: Record<string, string> = {
      user: 'Пользователь',
      worker: 'Worker',
      device: 'Устройство',
      external_api: 'API',
      simulation: 'Симуляция',
    };
    const label = sourceLabel[sourceType || 'user'] || sourceType || 'Пользователь';
    const confidence = sourceConfidence !== null && sourceConfidence !== undefined
      ? ` (${Math.round(sourceConfidence * 100)}%)`
      : '';
    return <Tag>{`${label}${confidence}`}</Tag>;
  };

  const handleConfirm = async (eventId: string) => {
    try {
      await confirmEvent({ saveId: saveId || '', eventId }).unwrap();
      message.success('Событие подтверждено');
      refetch();
    } catch (err: any) {
      message.error(err?.data?.message || 'Не удалось подтвердить событие');
    }
  };

  const handleReject = async (eventId: string, note?: string) => {
    try {
      await rejectEvent({ saveId: saveId || '', eventId, payload: { note } }).unwrap();
      message.success('Событие отклонено');
      refetch();
    } catch (err: any) {
      message.error(err?.data?.message || 'Не удалось отклонить событие');
    }
  };

  const openRejectModal = (eventId: string) => {
    setRejectModalEventId(eventId);
    setRejectNote('');
  };

  const submitRejectModal = async () => {
    if (!rejectModalEventId) return;
    await handleReject(rejectModalEventId, rejectNote || undefined);
    setRejectModalEventId(null);
    setRejectNote('');
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Культура / Шаблон',
      key: 'crop',
      render: (record: EventQueueItem) => {
        if (record.isBatch) {
          return (
            <div>
              <Text strong type="success">
                {record.templateName || 'Массовое мероприятие'}
              </Text>
              <br />
              <Text type="secondary" css={commonStyles.smallText}>
                {record.cropName || '—'}
              </Text>
            </div>
          );
        }
        return (
          <div>
            <Link to={`/saves/${saveId}/plantings/${record.plantingId}`}>
              <Text strong>{record.cropName || '—'}</Text>
            </Link>
            {record.cropLatinName && (
              <>
                <br />
                <Text type="secondary" css={commonStyles.smallText}>
                  {record.cropLatinName}
                </Text>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      render: (type: string, record: EventQueueItem) => (
        <div>
          <Space direction="vertical" size="small">
            <Space size="small">
              <Tag>{type}</Tag>
              <EventKindTag kind={record.kind} showIcon={true} />
            </Space>
            {record.isBatch && <Tag color="purple">Массовое</Tag>}
          </Space>
        </div>
      ),
    },
    {
      title: 'Локация',
      key: 'location',
      render: (record: EventQueueItem) => {
        if (record.isBatch) {
          return (
            <Text type="secondary" css={commonStyles.smallText}>
              Массовое мероприятие
            </Text>
          );
        }

        if (record.bedName) {
          return (
            <div css={commonStyles.smallText}>
              <Text strong>{record.bedName}</Text>
              {record.plotName && (
                <>
                  <br />
                  <Text type="secondary">{record.plotName}</Text>
                </>
              )}
            </div>
          );
        }

        if (record.plotName) {
          return (
            <div css={commonStyles.smallText}>
              <Text strong>{record.plotName}</Text>
              {record.fieldName && (
                <>
                  <br />
                  <Text type="secondary">{record.fieldName}</Text>
                </>
              )}
            </div>
          );
        }

        if (record.fieldName) {
          return <Text strong css={commonStyles.smallText}>{record.fieldName}</Text>;
        }

        return <Text type="secondary" css={commonStyles.smallText}>—</Text>;
      },
    },
    {
      title: 'Прогресс',
      key: 'progress',
      render: (record: EventQueueItem) => {
        if (!record.isBatch) {
          return <Text type="secondary">—</Text>;
        }
        const progress =
          record.batchTotal && record.batchTotal > 0
            ? Math.round(((record.batchCompleted || 0) / record.batchTotal) * 100)
            : 0;
        return (
          <div css={styles.progressContainer}>
            <Progress
              percent={progress}
              size="small"
              status={record.status === 'completed' ? 'success' : 'active'}
            />
            <Text type="secondary" css={commonStyles.tinyText}>
              {record.batchCompleted || 0} / {record.batchTotal || 0}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Детали задания',
      key: 'properties',
      render: (record: EventQueueItem) => {
        if (!record.properties || record.properties.length === 0) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <div>
            {record.properties.slice(0, 3).map((attr, idx) => {
              const value =
                attr.valueText ?? attr.valueNumber ?? (attr.valueBoolean ? 'Да' : 'Нет');
              return (
                <div key={idx} css={commonStyles.smallText}>
                  <Text type="secondary">{attr.propertyName}:</Text> <Text strong>{value}</Text>
                </div>
              );
            })}
            {record.properties.length > 3 && (
              <Text type="secondary" css={commonStyles.tinyText}>
                +{record.properties.length - 3} ещё
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: EventQueueStatus) => getStatusTag(status),
    },
    {
      title: 'Проверка',
      key: 'decision',
      render: (record: EventQueueItem) => (
        <Space direction="vertical" size={2}>
          {getDecisionTag(record.decisionState)}
          {getSourceTag(record.sourceType, record.sourceConfidence)}
          {record.decisionNote && (
            <Text type="secondary" css={commonStyles.tinyText}>
              {record.decisionNote.length > 48
                ? `${record.decisionNote.slice(0, 48)}...`
                : record.decisionNote}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Назначено',
      key: 'assignedTo',
      render: (record: EventQueueItem) =>
        record.assignedTo?.name ? (
          <Text css={commonStyles.smallText}>{record.assignedTo.name}</Text>
        ) : (
          <Text type="secondary" css={commonStyles.smallText}>
            Не назначено
          </Text>
        ),
    },
    {
      title: 'Плановая дата',
      dataIndex: 'plannedDate',
      key: 'plannedDate',
      render: (date: string) => {
        if (!date || date === '1970-01-01' || new Date(date).getFullYear() < 2000) {
          return <Text type="secondary" css={commonStyles.smallText}>—</Text>;
        }
        return <Text css={commonStyles.smallText}>{new Date(date).toLocaleDateString('ru-RU')}</Text>;
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (record: EventQueueItem) => {
        return (
          <Space direction="vertical" size="small">
            <Button size="small" onClick={() => setViewEventId(record.id)}>
              Подробнее
            </Button>
            {record.isBatch ? (
              <Button
                type="primary"
                size="small"
                onClick={() => navigate(`/saves/${saveId}/events/${record.id}`)}
              >
                Детали
              </Button>
            ) : (
              <>
                {(record.status === 'planned' ||
                  record.status === 'pending' ||
                  record.status === 'in_progress') &&
                  record.decisionState !== 'proposed' &&
                  record.decisionState !== 'rejected' &&
                  canCompleteEvents && (
                    <Button type="primary" size="small" onClick={() => setSelectedEvent(record)}>
                      Выполнить
                    </Button>
                  )}
                {record.decisionState === 'proposed' && canCompleteEvents && (
                  <>
                    <Popconfirm
                      title="Подтвердить событие?"
                      description="После подтверждения событие можно будет выполнить."
                      onConfirm={() => handleConfirm(record.id)}
                      okText="Подтвердить"
                      cancelText="Отмена"
                    >
                      <Button
                        type="primary"
                        size="small"
                        loading={confirmingEvent}
                        icon={<CheckCircleOutlined />}
                      >
                        Подтвердить
                      </Button>
                    </Popconfirm>
                    <Button
                      danger
                      size="small"
                      loading={rejectingEvent}
                      icon={<CloseCircleOutlined />}
                      onClick={() => openRejectModal(record.id)}
                    >
                      Отклонить
                    </Button>
                  </>
                )}
              </>
            )}
            {record.plantingId && (
              <Link to={`/saves/${saveId}/plantings/${record.plantingId}`}>
                <Button size="small">К посадке</Button>
              </Link>
            )}
          </Space>
        );
      },
    },
  ];

  if (!farmIdValue) {
    return (
      <Card>
        <Alert message="Не указан ID фермы" type="error" showIcon />
      </Card>
    );
  }

  return (
    <div css={styles.container}>
      <Card>
        <Space direction="vertical" css={commonStyles.fullWidth} size="large">
          {/* Заголовок */}
          <div css={styles.header}>
            <Title level={3} css={styles.title}>
              Задачи
            </Title>
            <Button onClick={() => refetch()}>Обновить</Button>
          </div>

          {/* Статистика */}
          {queueData && queueData.byStatus && (
            <Card size="small">
              <Space direction="vertical" css={commonStyles.fullWidth}>
                <div css={styles.statsRow}>
                  <Text strong>Прогресс выполнения</Text>
                  <Text>
                    {queueData.byStatus.completed} / {queueData.total}
                  </Text>
                </div>
                <Progress
                  percent={
                    queueData.total > 0
                      ? Math.round((queueData.byStatus.completed / queueData.total) * 100)
                      : 0
                  }
                  status="active"
                />
                <div css={styles.statsProgress}>
                  <Badge count={queueData.total} showZero css={css({ backgroundColor: '#1890ff' })}>
                    <div css={styles.badgePadding}>Всего</div>
                  </Badge>
                  <Badge
                    count={queueData.byStatus.planned}
                    showZero
                    css={css({ backgroundColor: '#d9d9d9' })}
                  >
                    <div css={styles.badgePadding}>Запланировано</div>
                  </Badge>
                  <Badge
                    count={queueData.byStatus.in_progress}
                    showZero
                    css={css({ backgroundColor: '#1890ff' })}
                  >
                    <div css={styles.badgePadding}>В процессе</div>
                  </Badge>
                  <Badge
                    count={queueData.byStatus.completed}
                    showZero
                    css={css({ backgroundColor: '#52c41a' })}
                  >
                    <div css={styles.badgePadding}>Завершено</div>
                  </Badge>
                </div>
              </Space>
            </Card>
          )}

          {/* Фильтры */}
          <Space wrap>
            <Select
              css={css({ width: 200 })}
              placeholder="Назначение"
              value={assigneeFilter}
              onChange={setAssigneeFilter}
            >
              <Option value="all">Все задачи</Option>
              <Option value="me">Назначено на меня</Option>
              <Option value="unassigned">Не назначенные</Option>
            </Select>

            <Select
              css={css({ width: 200 })}
              placeholder="Фильтр по статусу"
              value={statusFilter}
              onChange={setStatusFilter}
              disabled={!!decisionFilter}
              allowClear
            >
              <Option value="planned">Запланировано</Option>
              <Option value="pending">Ожидает</Option>
              <Option value="in_progress">Выполняется</Option>
              <Option value="completed">Завершено</Option>
              <Option value="cancelled">Отменено</Option>
            </Select>

            <Select
              css={css({ width: 220 })}
              placeholder="Решение"
              value={decisionFilter}
              onChange={(v) => setDecisionFilter(v)}
              allowClear
            >
              <Option value="proposed">Требуют подтверждения</Option>
            </Select>

            <Select
              css={css({ width: 200 })}
              placeholder="Фильтр по типу"
              value={typeFilter}
              onChange={setTypeFilter}
              allowClear
            >
              <Option value="measurement">Измерение</Option>
              <Option value="inspection">Осмотр</Option>
              <Option value="pruning">Обрезка</Option>
              <Option value="watering">Полив</Option>
              <Option value="fertilizing">Подкормка</Option>
              <Option value="pest_control">Борьба с вредителями</Option>
              <Option value="weeding">Прополка</Option>
              <Option value="general">Общее</Option>
            </Select>
          </Space>

          {/* Таблица */}
          {isLoading ? (
            <div css={[commonStyles.textCenter, styles.spinnerContainer]}>
              <Spin size="large" />
            </div>
          ) : error ? (
            <Alert message="Ошибка загрузки" description="Не удалось загрузить очередь событий" type="error" showIcon />
          ) : (
            <Table
              dataSource={groupedEvents}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 20 }}
              locale={{ emptyText: 'Нет событий' }}
            />
          )}
        </Space>
      </Card>

      {/* Модалка завершения события */}
      {selectedEvent && (
        <CompleteEventModal
          open={!!selectedEvent}
          event={selectedEvent}
          farmId={farmId || ''}
          onClose={() => setSelectedEvent(null)}
          onSuccess={() => {
            refetch();
            setSelectedEvent(null);
          }}
        />
      )}

      {/* Модалка просмотра деталей события */}
      <EventDetailsModal
        eventId={viewEventId}
        farmId={farmId || ''}
        open={!!viewEventId}
        onClose={() => setViewEventId(null)}
        canEdit={canCompleteEvents}
      />

      <Modal
        title="Отклонить событие"
        open={!!rejectModalEventId}
        onCancel={() => setRejectModalEventId(null)}
        onOk={submitRejectModal}
        okText="Отклонить"
        okButtonProps={{ danger: true, loading: rejectingEvent }}
        cancelText="Отмена"
      >
        <Space direction="vertical" css={commonStyles.fullWidth}>
          <Text type="secondary">Укажите причину (опционально)</Text>
          <Input.TextArea
            rows={4}
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="Например: низкая уверенность источника"
            maxLength={500}
          />
        </Space>
      </Modal>
    </div>
  );
};
